import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { RefreshCw, Wifi } from 'lucide-react';

export default function ServiceWorkerStatus() {
  const { t } = useTranslation();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [, setQueuedRequests] = useState(0);

  useEffect(() => {
    // Listen for service worker events
    const handleUpdateAvailable = () => setUpdateAvailable(true);
    const handleOfflineReady = () => setOfflineReady(true);

    window.addEventListener('sw-update-available', handleUpdateAvailable);
    window.addEventListener('sw-offline-ready', handleOfflineReady);

    // Listen for service worker messages about queued requests
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'QUEUE_PROCESSED') {
        setQueuedRequests(event.data.failed || 0);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      window.removeEventListener('sw-update-available', handleUpdateAvailable);
      window.removeEventListener('sw-offline-ready', handleOfflineReady);

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, []);

  const handleUpdate = () => {
    window.location.reload();
  };

  const handleDismissOfflineReady = () => {
    setOfflineReady(false);
  };

  const handleDismissUpdate = () => {
    setUpdateAvailable(false);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {/* Update Available */}
      {updateAvailable && (
        <Card className="w-80">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <RefreshCw className="h-5 w-5 text-blue-500 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium">{t('pwa.update.newVersion.title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('pwa.update.newVersion.description')}
                </p>
                <div className="flex gap-2 mt-3">
                  <Button onClick={handleUpdate} size="sm">
                    {t('pwa.update.reload')}
                  </Button>
                  <Button onClick={handleDismissUpdate} variant="outline" size="sm">
                    {t('pwa.update.later')}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Offline Ready */}
      {offlineReady && (
        <Card className="w-80">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Wifi className="h-5 w-5 text-green-500 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium">{t('pwa.update.offlineReady.title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('pwa.update.offlineReady.description')}
                </p>
                <div className="flex gap-2 mt-3">
                  <Button onClick={handleDismissOfflineReady} variant="outline" size="sm">
                    {t('common.ok')}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
